import { useCallback, useMemo } from 'react';
import { useDailyWorkflowStorage } from './UseDailyWorkflowStorage';
import { useCards } from './UseCards';
import { useInlinePriceEdit } from './UseInlinePriceEdit';
import { useCardJobs } from './UseCardJobs';
import { useServerDaily } from './UseServerDaily';
import { useServerDropReviews } from './UseServerDropReviews';
import type { JobStatus } from '../types/job';
import { dailyDoneGate, isReviewGateSkip } from '../utils/daily';
import { appMessage as message } from '../utils/appMessage';

// ─── useCardData (공개 API) ───────────────────────────────────────────────────

/**
 * 서버 데이터 관련 상태를 모두 담당하는 훅.
 * cards(UseCards.ts) / edit(UseInlinePriceEdit.ts) / jobs(UseCardJobs.ts) 세 관심사를
 * 한 곳에서 조합하여 CardPageContext 에 제공한다 — 예전엔 이 세 훅이 전부 이 파일
 * 하나(393줄)에 있었는데, 로직 자체는 이미 함수 단위로 잘 분리돼 있어서 파일만
 * 3개로 옮겼다(2026-08-11, 기계적 이동, 로직 변경 없음).
 *
 * [3번 개선] updateDone / naverDone / pendingConfirmIds 를 날짜 기반 localStorage 에
 * 영속화하여 새로고침·세션만료 후에도 오늘 진행분이 유지된다.
 *
 * edit 는 반환 객체(data)에 일부러 포함하지 않고 별도로 반환한다(2026-08-13) — 가격
 * 수정 시작/종료마다 editingId 가 바뀌는데, 예전엔 이게 data 전체의 참조를 새로 만들어
 * CardPageContext 구독자 전부(PriceCell 최대 50개 포함)를 리렌더시켰다. jobs 는 여전히
 * edit.reconcilePendingDrops 를 필요로 하므로 훅 호출 순서(cards → edit → jobs)는
 * 그대로 유지 — edit 를 data 메모이제이션 대상에서만 뺀다. CardPageContext 가 이 edit
 * 를 별도 컨텍스트(CardEditContext)로 제공한다.
 */
export function useCardData() {
  const storage = useDailyWorkflowStorage();

  // hasLoadedOnce("성공한 조회가 있었는지")·setInlineError(재조회 실패 토스트 런타임 토글)도
  // 화면까지 그대로 전달한다 — 화면이 `cards.length === 0` 으로 첫 조회 실패를 판단하면
  // 진짜 0건 뒤 재조회 실패가 전체 오류 화면으로 둔갑하고, 토스트 억제를 훅에 전역 고정하면
  // 배너를 그리지 않는 화면의 실패가 묻힌다(2026-10-21 리뷰).
  const {
    cards, setCards, loading, reload, replaceCard, mergeResults, removeCard, removeCards,
    error, retry, hasLoadedOnce, setInlineError, loadVersion,
  } = useCards();
  const baseEdit = useInlinePriceEdit(replaceCard, storage);
  const baseJobs = useCardJobs(
    mergeResults, baseEdit.reconcilePendingDrops, baseEdit.removePendingMany, storage, reload,
  );
  // 하락 검토를 서버에(감사 #566, 백엔드 PR #79) — 검토 동작을 감싸 KEEP · APPLY 를 남기고, 서버의 검토 대기를 하락 확인 대기로.
  const edit = useServerDropReviews(baseEdit, { activeJobId: baseJobs.activeJobId });

  // ① 오늘 상태 · 아침 서버 체인(감사 #566, 백엔드 PR #78) — 체인이 끝나면 카드를 다시 받는다(동기화 · 상승분 반영 결과까지).
  const onChainFinished = useCallback(() => { void reload(); }, [reload]);
  const daily = useServerDaily({
    activeJobId: baseJobs.activeJobId, setActiveJobId: baseJobs.setActiveJobId, onChainFinished,
  });

  // ③ 이 서버 검토 게이트로 건너뛴 카드(실패가 아니라 검토가 필요한 것)를 하락 검토로 안내한다(백엔드 PR #79).
  const baseComplete = baseJobs.handleJobComplete;
  const handleJobComplete = useCallback((job: JobStatus) => {
    baseComplete(job);
    if (job.type !== 'SYNC_NAVER' || job.status === 'FAILED' || !job.results) return;
    const gated = job.results.filter((c) => isReviewGateSkip(c.naverError)).length;
    if (gated > 0) {
      message.warning({
        key: 'review-gate-skip',
        content: `검토가 필요해 건너뛴 카드 ${gated.toLocaleString()}건 — 하락 검토(확인 필요) · 가격 상승 탭에서 적용하면 다음 ③ 에 들어갑니다.`,
        duration: 10,
      });
    }
  }, [baseComplete]);

  // 서버 상태와 OR — 다른 PC · 07:30 서버 체인이 받은 것도 완료, 체인이 도는 동안은 ① 진행 중.
  // 단 서버 '오늘 받기 완료' 는 서버 검토 목록(하락 확인 대기)을 받은 뒤에만 믿는다(클라우드 리뷰 높음, 감사 card-controltower#656) —
  // 다른 PC 의 하락 확인 대기는 그 목록으로만 채워지는데, 목록을 못 받으면(백엔드 #78 만 배포 · drop-reviews 오류 · 네트워크)
  // 07:30 체인 완료만으로 ②③ 이 열려 ③ 이 하락을 검토 없이 보냈다. 서버는 완료인데 목록이 없으면 ③ 을 막고 다시 받게 한다.
  const { serverReview } = edit;
  const { updateDone, reviewListMissing } = dailyDoneGate({
    localDone: baseJobs.updateDone, serverDone: daily.serverUpdateDone,
    reviewSupported: serverReview.supported, reviewListLoaded: serverReview.list != null,
  });
  const retryReviewList = serverReview.refresh;
  const jobs = useMemo(() => ({
    ...baseJobs,
    updateDone,
    /** 서버는 오늘 받기 완료인데 하락 검토 목록을 받지 못했다 — ③ 을 막는다(#656). */
    reviewListMissing,
    /** 하락 검토 목록 다시 받기(reviewListMissing 일 때 '다시 시도'). */
    retryReviewList,
    priceUpdating: baseJobs.priceUpdating || daily.chainRunning,
    handleJobComplete,
    /** ① 아침 서버 체인 — 'unsupported'(옛 서버)면 부른 쪽이 예전 브라우저 사슬로. */
    startMorningChain: daily.startMorningChain,
    serverDaily: daily.daily,
    refreshDaily: daily.refreshDaily,
  }), [baseJobs, updateDone, reviewListMissing, retryReviewList, daily.chainRunning, daily.startMorningChain, daily.daily,
    daily.refreshDaily, handleJobComplete]);

  const data = useMemo(
    () => ({
      cards, setCards, loading, reload, mergeResults, removeCard, removeCards, jobs,
      error, retry, hasLoadedOnce, setInlineError, loadVersion,
    }),
    [
      cards, setCards, loading, reload, mergeResults, removeCard, removeCards, jobs,
      error, retry, hasLoadedOnce, setInlineError, loadVersion,
    ],
  );

  return { data, edit };
}
